'use client';

import { useState, useRef, useCallback } from 'react';
import Image from 'next/image';

const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'svg', 'pdf', 'ai', 'eps'];
const MAX_SIZE = 25 * 1024 * 1024; // 25MB

interface ArtworkUploadProps {
  artworkUrl: string;
  artworkFilename: string;
  onUpload: (url: string, filename: string) => void;
  onRemove: () => void;
  guidanceHeading?: string;
  guidanceImageSrc?: string;
  guidanceImageAlt?: string;
}

export default function ArtworkUpload({
  artworkUrl,
  artworkFilename,
  onUpload,
  onRemove,
  guidanceHeading,
  guidanceImageSrc,
  guidanceImageAlt,
}: ArtworkUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `File type .${ext} not supported. Use PNG, JPG, WEBP, PDF, AI, EPS, or SVG.`;
    }
    if (file.size > MAX_SIZE) {
      return 'File too large. Maximum 25MB.';
    }
    return null;
  };

  const uploadFile = useCallback(async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Upload failed');
        return;
      }

      onUpload(data.url, data.filename);
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [onUpload]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const guidanceBlock = guidanceImageSrc ? (
    <div className="mb-3 max-w-full" style={{ width: 'min(100%, calc(100vw - 5rem))' }}>
      {guidanceHeading && (
        <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-black">
          {guidanceHeading}
        </span>
      )}
      <Image
        src={guidanceImageSrc}
        alt={guidanceImageAlt || guidanceHeading || 'Artwork upload guidance'}
        width={1536}
        height={1024}
        sizes="(min-width: 1024px) 26rem, 100vw"
        style={{ width: '100%', height: 'auto' }}
        className="block h-auto w-full max-w-full border border-[var(--color-border)] object-contain"
      />
    </div>
  ) : null;

  // Already uploaded — show success state
  if (artworkUrl) {
    return (
      <div className="min-w-0">
        {guidanceBlock}
        <span className="block text-sm font-medium uppercase tracking-wider mb-3">
          Artwork
        </span>
        <div className="flex items-center justify-between border border-[var(--color-border)] px-4 py-3 bg-[var(--color-off-white)]">
          <div className="flex items-center gap-3 min-w-0">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-green-600 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
            <span className="text-sm truncate">{artworkFilename}</span>
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="text-sm text-[var(--color-text-secondary)] underline hover:text-black transition-colors flex-shrink-0 ml-3"
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      {guidanceBlock || (
        <span className="block text-sm font-medium uppercase tracking-wider mb-3">
          Upload Artwork
          <span className="font-normal text-[var(--color-text-secondary)] ml-1">(optional)</span>
        </span>
      )}

      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        style={{ width: 'min(100%, calc(100vw - 5rem))' }}
        className={`w-[calc(100vw-5rem)] max-w-full border border-dashed px-4 py-4 text-center cursor-pointer transition-colors lg:w-full ${
          isDragging
            ? 'border-black bg-gray-50'
            : 'border-[var(--color-border)] hover:border-black'
        } ${isUploading ? 'pointer-events-none opacity-60' : ''}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".png,.jpg,.jpeg,.webp,.svg,.pdf,.ai,.eps"
          onChange={handleFileSelect}
          className="hidden"
        />

        {isUploading ? (
          <div className="flex flex-col items-center gap-2">
            <svg className="animate-spin h-6 w-6 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="text-sm text-[var(--color-text-secondary)]">Uploading...</span>
          </div>
        ) : (
          guidanceBlock ? (
            <div className="text-sm">
              <p className="font-semibold text-black">Upload image or print file</p>
              <p className="mt-1 break-words text-xs text-[var(--color-text-secondary)]">
                PNG, JPG, WEBP, SVG, PDF, AI, EPS up to 25.0MB
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 text-center sm:flex-row sm:gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 shrink-0 text-[var(--color-text-muted)]">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <span className="text-sm">Drag & drop or click to browse</span>
              <span className="text-xs text-[var(--color-text-muted)]">
                (PNG, JPG, WEBP, PDF, AI, EPS - 25MB max)
              </span>
            </div>
          )
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600 mt-2">{error}</p>
      )}
    </div>
  );
}
